import styles from './Pagination.module.css';

/**
 * The offset pagination footer: a per-page segmented control on the left, and
 * Previous / "Page x of y" / Next on the right.
 *
 * Extracted from `CategoryListing` (Story 7-FU-C) when the order-history list
 * (Story 9.4) needed the same control, so the two paged screens cannot drift into
 * offering different sizes or different keyboard and screen-reader behaviour. The
 * markup and accessible names are carried over verbatim, which is what lets the
 * existing `CategoryListing.pagination.spec.tsx` keep passing unchanged.
 *
 * Presentational only: it owns no page state and fetches nothing. The caller holds
 * the committed page and size and decides what a change means, because the two
 * screens differ there (the catalogue puts paging in the URL; order history keeps
 * it in component state alongside the active depot).
 */
export interface PaginationProps {
  /** 1-based, and the page actually rendered, not one requested and still in flight. */
  readonly page: number;
  readonly totalPages: number;
  /** The committed page size; must be one of `pageSizeOptions` to read as selected. */
  readonly pageSize: number;
  readonly pageSizeOptions: readonly number[];
  readonly onPageChange: (page: number) => void;
  readonly onPageSizeChange: (pageSize: number) => void;
  /**
   * A page request is in flight. Disables every control, so a click cannot quietly
   * discard the request already running (PR #252 review, r3), and marks Next busy.
   */
  readonly busy?: boolean;
  /** Distinguishes the two `per-page-label` ids when both screens mount at once. */
  readonly idPrefix?: string;
}

export function Pagination({
  page,
  totalPages,
  pageSize,
  pageSizeOptions,
  onPageChange,
  onPageSizeChange,
  busy = false,
  idPrefix = 'pagination',
}: PaginationProps) {
  const labelId = `${idPrefix}-per-page-label`;
  const canPrev = page > 1 && !busy;
  const canNext = page < totalPages && !busy;

  return (
    <div
      className={styles.paginationFooter}
      role="group"
      aria-label="Results summary and pagination"
    >
      <div className={styles.perPage}>
        <span className={styles.perPageLabel} id={labelId}>
          Per page
        </span>
        <div className={styles.perPageGroup} role="group" aria-labelledby={labelId}>
          {pageSizeOptions.map((option) => (
            <button
              key={option}
              type="button"
              className={`${styles.perPageButton} ${
                option === pageSize ? styles.perPageButtonActive : ''
              }`}
              aria-pressed={option === pageSize}
              aria-label={`${option} per page`}
              disabled={busy}
              onClick={() => onPageSizeChange(option)}
            >
              {option}
            </button>
          ))}
        </div>
      </div>
      <div className={styles.pager}>
        <button
          type="button"
          className={styles.pagerButton}
          onClick={() => onPageChange(page - 1)}
          disabled={!canPrev}
        >
          Previous
        </button>
        <span className={styles.pageIndicator} role="status">
          Page {page} of {totalPages}
        </span>
        <button
          type="button"
          className={styles.pagerButton}
          onClick={() => onPageChange(page + 1)}
          disabled={!canNext}
          aria-busy={busy}
        >
          Next
        </button>
      </div>
    </div>
  );
}
